import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getSignatureAttestation, signerAttestation } from '../services/signatureAttestationService';
import SignaturePad from './SignaturePad';
import { formatDateTime } from './devisUtils';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import './Signature.css';

/**
 * Signature de son attestation de formation, atteinte par le lien reçu par
 * email. Une page par personne : le jeton désigne le signataire, la page ne
 * montre jamais l'attestation des autres.
 *
 * Même gabarit que l'émargement : le geste attendu — signer — ouvre la page,
 * les informations viennent ensuite.
 */
function SignatureAttestation() {
  const { token } = useParams();
  const [attestation, setAttestation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [envoi, setEnvoi] = useState(false);
  const [erreurSignature, setErreurSignature] = useState(null);
  // Le nom s'écrit à la main, comme sur le papier : c'est lui qui s'imprime
  // à côté de la signature.
  const [nomSaisi, setNomSaisi] = useState('');

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    return getSignatureAttestation(token)
      .then((res) => {
        setAttestation(res.data);
        setNomSaisi((n) => n || res.data?.signataire?.nom || '');
      })
      .catch((err) => setError(err.message || 'Lien invalide.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    charger();
  }, [charger]);

  async function valider(image) {
    setEnvoi(true);
    setErreurSignature(null);
    try {
      const res = await signerAttestation(token, { image, nom: nomSaisi });
      setAttestation(res.data);
    } catch (err) {
      setErreurSignature(err.message || 'La signature n’a pas pu être enregistrée.');
    } finally {
      setEnvoi(false);
    }
  }

  const signee = Boolean(attestation?.signeLe);

  return (
    <div className="devis-page devis-page-scroll signature-doc">
      <header className="emargement-entete">
        <h1 className="devis-title">Attestation de formation</h1>
        <p className="devis-subtitle">
          {attestation ? attestation.session.intitule : 'Signature en ligne'}
        </p>
      </header>

      {loading && <div className="signature-etat">Chargement de votre attestation...</div>}
      {!loading && error && <div className="signature-etat signature-etat-erreur">{error}</div>}

      {!loading && !error && attestation && (
        <>
          {signee ? (
            <div className="signature-etat signature-etat-succes emargement-succes">
              <p className="emargement-succes-titre">Attestation signée, merci !</p>
              <p className="emargement-succes-detail">
                Votre signature a été enregistrée le {formatDateTime(attestation.signeLe)}. Vous
                pouvez fermer cette page.
              </p>
            </div>
          ) : (
            <section className="emargement-signer">
              <p className="emargement-consigne">
                Vérifiez votre nom, puis signez dans le cadre ci-dessous.
              </p>
              <label className="signature-champ signature-champ-large">
                <span className="signature-champ-label">Nom et prénom</span>
                <input
                  type="text"
                  className="signature-champ-input"
                  value={nomSaisi}
                  onChange={(e) => setNomSaisi(e.target.value)}
                  placeholder="Comme il doit figurer sur l’attestation"
                  maxLength={255}
                  disabled={envoi}
                />
              </label>
              {erreurSignature && (
                <div className="signature-etat signature-etat-erreur">{erreurSignature}</div>
              )}
              <SignaturePad onValider={valider} validation={envoi} />
              <p className="emargement-attestation">
                En signant, vous reconnaissez avoir suivi cette formation. Votre signature figurera
                sur votre attestation.
              </p>
            </section>
          )}

          <div className="devis-cartes">
            <div className="devis-carte">
              <div className="devis-carte-titre">PARTICIPANT</div>
              <dl>
                <dt>Nom</dt>
                <dd>{attestation.signataire.nom}</dd>
                <dt>Qualité</dt>
                <dd>{attestation.signataire.qualite}</dd>
              </dl>
            </div>
            <div className="devis-carte">
              <div className="devis-carte-titre">FORMATION</div>
              <dl>
                <dt>Client</dt>
                <dd>{attestation.session.societe || '—'}</dd>
                <dt>Dates</dt>
                <dd>{attestation.session.dates}</dd>
                <dt>Durée</dt>
                <dd>{attestation.session.duree}</dd>
                {attestation.session.formateur && (
                  <>
                    <dt>Formateur</dt>
                    <dd>{attestation.session.formateur}</dd>
                  </>
                )}
              </dl>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SignatureAttestation;
